import * as React from "react";
import Moment from "react-moment";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { usePerformAction } from "react-remote-data-hooks";
import api from "../../api";
import {
  AccountType,
  AppPermissions,
  Checklist,
  convertPascalCaseToWords,
  DateFormats,
  State,
  useHasPermission,
  Vehicle,
  VehicleStatus,
  VINStatus,
} from "../../core";
import OwnerBlock from "../leads/components/assign-users/OwnerBlock";
import { Project } from "../../core/api/models/project";
import { Property } from "../../core/api/models/property";

interface IGeneralInfoTabProps {
  property: Property;
  reload?: () => void;
}

const GeneralInfoTab: React.FunctionComponent<IGeneralInfoTabProps> = (
  props
) => {
  const [changeStatusView, setChangeStatusView] = React.useState(false);
  const [changeTraderView, setChangeTraderView] = React.useState(false);
  const [
    showDeliveryStatusUpdateForm,
    setShowDeliveryStatusForm,
  ] = React.useState<boolean>(false);
  const [
    showRegistrationStatusUpdateForm,
    setRegistrationStatusShowUpdateForm,
  ] = React.useState<boolean>(false);
  const authState = useSelector((state: State) => state.authUser);

  const {
    isPerforming: isUpdatingTrader,
    performAction: updateTrader,
  } = usePerformAction(
    async (data: {
      isTraderVin: boolean;
      gmReportedDate?: Date;
      registrationDeadline?: Date;
    }) => {
      return api.vehicle.updateTraderDetails(props.property._id!, {
        isTraderVin: data.isTraderVin,
        gmReportedDate: data.isTraderVin ? data.gmReportedDate : undefined,
        registrationDeadline: data.isTraderVin
          ? data.registrationDeadline
          : undefined,
      });
    },
    () => {
      toastr.success("Success", `Trader details updated successfully`);
      props.reload?.();
    },
    (err) => {
      toastr.error(
        "Error",
        (err as any).response?.data?.data?.message ||
          "Unable to update trader details"
      );
    }
  );

  const decodeHtmlEntities = (html: string) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    return textarea.value;
  };
  
  return (
    <div className="px-4 pt-3 pb-2" style={{ maxWidth: "650px" }}>
      <div className="row mb-3 font-md font-weight-bold">General Info</div>
      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          Property Name
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {props.property.propertyName}
        </div>
      </div>
      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          Property Type
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {props.property.propertyType}
        </div>
      </div>
      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          Property Featured
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {props.property.propertyFeatured}
        </div>
      </div>
      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          Property Purpose
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          <div
            dangerouslySetInnerHTML={{
              __html: props.property.propertyPurpose || "",
            }}
          ></div>
        </div>
      </div>
      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          Property Type Name
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {decodeHtmlEntities(props.property.propertyTypeName)}
        </div>
      </div>
      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          Property Description
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {props.property.propertyDescription}
        </div>
      </div>
      <div className="row my-3 pt-4 font-md font-weight-bold">
        Project Details
      </div>
      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          Project Name
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {props.property.projectDetails.projectName}
        </div>
      </div>
      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          Project Current Status
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {convertPascalCaseToWords(
            props.property.projectDetails.projectCurrentStatus
          )}
        </div>
      </div>
      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          Project Added Date
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {(() => {
            const date = new Date(
              props.property.projectDetails.projectAddedDate
            );
            const year = date.getFullYear();
            const month = ("0" + (date.getMonth() + 1)).slice(-2);
            const day = ("0" + date.getDate()).slice(-2);
            return `${year}-${month}-${day}`;
          })()}
        </div>
      </div>

      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          Project Description
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {props.property.projectDetails.projectDescription
            .replace(/<br\s*\/?>/g, "")
            .replace(/&lt;/g, "")
            .replace(/&gt;/g, "")
            .replace(/~/g, "")
            .replace(/<br>/g, "")
            .replace(/\//g, "")
            .replace(/\bbr\b/g, "")}
        </div>
      </div>

      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          Project Builder Name
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {props.property.projectDetails.projectBuilderName.developer}
        </div>
      </div>
      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          Project Type Name
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {props.property.projectDetails.projectTypeName}
        </div>
      </div>

      <div className="row my-3 pt-4 font-md font-weight-bold">
        Location Detail
      </div>
      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          Address
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {props.property.propertyAddress1}
        </div>
      </div>
      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          Country
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {props.property.countryName}
        </div>
      </div>
      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          State
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {props.property.stateName}
        </div>
      </div>
      <div className="row border-bottom py-2">
        <div className="col-md-3 col-5 font-sm text-gray-500 pl-0 py-1">
          City
        </div>
        <div className="col-md-9 col-7 font-sm font-weight-semibold py-1">
          {props.property.cityName}
        </div>
      </div>
    </div>
  );
};

export default GeneralInfoTab;
